import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createAboutUsVideoSkill: Skill = {
    name: SKILL_NAMES.createAboutUsVideo,
    description:
        'About Us / company-story video — origin, milestones, mission, where they are today.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'About us',
        blurb: 'Introduce your team, story or mission.',
        icon: 'diversity_3',
        gradient: ['#96764F', '#B08F5A'],
    },
    instructions: `# Create About Us Video

The company-story format: a short documentary, not an ad — measured storytelling
over an AI voiceover.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill adds the
story formula, and its rules win where they conflict.

## Tone — professional storytelling, not a pitch
- Write the narration the way a measured, professional narrator tells a story —
  confident and warm, never hyped. No ad-style language ("amazing",
  "revolutionary", exclamation points) unless the brand voice calls for it.
- No hard sales close: the goal is understanding and trust. A light, natural
  closing thought is fine; a string of imperative CTAs is not.
- The tone is measured; the cut pacing is not — those are separate things. Target
  roughly 45 seconds of narration: a brief, well-told summary, not an exhaustive
  history. Split long sentences across overlays at natural clause breaks so no
  overlay lingers.

## Narrative arc — chronological acts, not a repeated pair cycle
This is one continuous story told in acts:
1. **Origin** — where it started and why (a founding moment, a noticed problem).
2. **Growth / milestones** — key moments and turning points. Several distinct
   milestones are a natural fit for a listicle-style design.
3. **Mission / values** — what it stands for now, in its own words.
4. **Today / closing** — where things stand, closing the arc in the same measured
   tone rather than pivoting into a sales ask.
- If the material doesn't map to all four acts, use the ones that apply — but keep
  what's there in roughly chronological order.
- Transition between acts inside the narration itself ("Years later...",
  "Today...") covered by a chapter-title overlay — don't break the single
  voiceover scene group.

## Media — authenticity over stock
- The user's real photos and footage (team, workspace, product, founder) come
  first — place them before reaching for stock; load the \`${SKILL_NAMES.findUserMedia}\` skill
  to go through what they have. Authentic media is far more credible here.
- Stock only for beats the uploads genuinely can't cover — and favor footage that
  could pass as real b-roll of the subject, not obviously generic stock.
- Don't force an upload into a beat where it doesn't fit; unused beats stock.

## Text
- Segment the narration so names, dates, places, and key facts get their own
  overlay and land clearly on screen.
- Not every beat needs to be text-heavy — on calmer narrative beats, light or
  minimal on-screen text lets the voiceover and visuals carry the moment.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- Confirm the story reads in a sensible chronological/thematic order (no act jumping).
- Confirm the tone stays measured throughout and fix any line that reads like ad copy.
- Confirm authentic media used wherever provided; stock (where used) doesn't clash next
  to it.
- Confirm every overlay breaks at a natural clause boundary.`,
};
